import { MemberAutocomplete } from "@components/common/member-autocomplete/member-autocomplete";
import { isWorkspaceAdmin } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { isCompanyMember } from "@custom-types/member-type-guards";
import { Dialog, Text } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { companyMembersSelector } from "@store/members/members-selector";
import { useAppSelector } from "@store/store-helper";
import { toGroupMember } from "@utils/member-utils";
import { hasXgSeatPermission } from "@utils/permission-utils";
import { useMemo, useState } from "react";

/**
 * Returns the members of the workspace who can become the new manager: everybody but the member
 * stepping down who holds a Sphere XG seat, which a manager needs, and is an admin when only admins
 * can be the manager. They are shaped as group members, which is what the autocomplete lists.
 */
function getManagerCandidates(workspaceMembers, memberSteppingDownIdentity, isAdminOnly) {
  return workspaceMembers.filter(isCompanyMember).filter(candidate => candidate.identity !== memberSteppingDownIdentity && hasXgSeatPermission({
    member: candidate
  }) && (!isAdminOnly || isWorkspaceAdmin(candidate))).map(toGroupMember);
}
/**
 * Dialog that asks for a new manager in place of the member losing their Sphere XG seat. The new
 * manager is picked from the members of the workspace holding a Sphere XG seat, since a manager
 * needs one too, and cannot be invited by email.
 */
export function SelectManagerDialog({
  member,
  title,
  description,
  dataTestId,
  isAdminOnly = false,
  areCandidatesLoading = false,
  children,
  onClose,
  onAssign
}) {
  const [selectedMember, setSelectedMember] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const workspaceMembers = useAppSelector(companyMembersSelector);
  // Kept as the same list while the members do not change, since the autocomplete resets its
  // options, and with them what the user typed, whenever it gets a new one
  const candidates = useMemo(() => getManagerCandidates(workspaceMembers, member.identity, isAdminOnly), [workspaceMembers, member.identity, isAdminOnly]);
  return <Dialog open title={title} confirmText="Assign" onConfirm={assignManager} isConfirmDisabled={!selectedMember} showSpinner={isLoading} onCancel={onClose} dataTestId={dataTestId}>
      <Stack sx={{
      gap: 2
    }}>
        <Text variant="bodyM">{description}</Text>
        <Stack sx={{
        gap: 0.5
      }}>
          <MemberAutocomplete members={candidates} selectedMember={selectedMember} onOptionChange={setSelectedMember} isDisabled={isLoading || areCandidatesLoading} canInviteByEmail={false} placeholder="Choose a member..." />
          <Text variant="bodyS" sx={{
          color: "text.secondary"
        }}>
            {isAdminOnly ? "Only admins in the workspace are listed" : "Only members with a Sphere XG seat are listed"}
          </Text>
        </Stack>
        {children}
      </Stack>
    </Dialog>;

  /** Makes the selected member the new manager, keeping the dialog open when that failed */
  async function assignManager() {
    // Only listed members can be selected, since typing an email is not offered
    if (!selectedMember || typeof selectedMember === "string") {
      return;
    }
    setIsLoading(true);
    if (!(await onAssign(selectedMember))) {
      setIsLoading(false);
    }
  }
}